import { americanToImplied, impliedToAmerican } from './liveOddsVnextModel.js'
import { MARKET_QUALITY_WEIGHTS } from './liveOddsFairValueV1.js'

function finiteAmerican(value) {
  const parsed = Number(value)
  return Number.isFinite(parsed) && (parsed >= 100 || parsed <= -100) ? parsed : null
}

function bookWeight(book, settings) {
  if (settings?.weighting !== 'market_quality') return 1
  const configured = Number(settings?.bookWeights?.[book] ?? MARKET_QUALITY_WEIGHTS[book] ?? 1)
  return configured > 0 ? configured : 0
}

function weightedMean(items) {
  const valid = items.filter((item) => Number.isFinite(item.value) && item.weight > 0)
  const totalWeight = valid.reduce((sum, item) => sum + item.weight, 0)
  if (!(totalWeight > 0)) return null
  return valid.reduce((sum, item) => sum + item.value * item.weight, 0) / totalWeight
}

export function parseEventTeams(eventName) {
  const value = String(eventName || '').trim()
  for (const separator of [/\s+@\s+/, /\s+vs\.?\s+/i, /\s+v\s+/i]) {
    const parts = value.split(separator).map((part) => part.trim()).filter(Boolean)
    if (parts.length === 2) return { away: parts[0], home: parts[1] }
  }
  return { away: value, home: '' }
}

export function buildRawAverage(cells, settings = {}) {
  const samples = Object.values(cells || {}).map((cell) => {
    const american = finiteAmerican(cell?.row?.american)
    return {
      book: cell?.row?.book,
      value: american == null ? null : americanToImplied(american),
      weight: bookWeight(cell?.row?.book, settings),
    }
  }).filter((sample) => Number.isFinite(sample.value))
  const probability = weightedMean(samples)
  return {
    probability,
    american: probability == null ? null : impliedToAmerican(probability),
    books: samples.map((sample) => sample.book).filter(Boolean),
    bookCount: samples.length,
  }
}

export function buildMarketHold(market, settings = {}) {
  const selections = market?.selections || []
  const samples = (market?.books || []).map((book) => {
    const prices = selections.map((selection) => finiteAmerican(market?.cells?.[selection]?.[book]?.row?.american))
    if (prices.some((price) => price == null) || prices.length < 2) return null
    const implied = prices.map(americanToImplied)
    return {
      book,
      value: implied.reduce((sum, value) => sum + value, 0) - 1,
      weight: bookWeight(book, settings),
    }
  }).filter(Boolean)
  return {
    probability: weightedMean(samples),
    books: samples.map((sample) => sample.book),
    bookCount: samples.length,
  }
}

export function buildSelectionMetrics(market, selection, selectedBookCount, settings = {}) {
  const cells = market?.cells?.[selection] || {}
  const priced = Object.values(cells).filter((cell) => finiteAmerican(cell?.row?.american) != null)
  const comparable = market?.integrityStatus === 'ok'
  const best = priced.reduce((value, cell) => Math.max(value, Number(cell.row.american)), -Infinity)
  const fair = market?.fair?.bySelection?.[selection] || null
  return {
    comparable,
    integrityStatus: market?.integrityStatus || 'unknown',
    bestAmerican: comparable && Number.isFinite(best) ? best : null,
    bestBooks: comparable ? priced.filter((cell) => Number(cell.row.american) === best).map((cell) => cell.row.book) : [],
    average: comparable ? buildRawAverage(cells, settings) : { probability: null, american: null, books: [], bookCount: 0 },
    fair,
    hold: comparable ? buildMarketHold(market, settings) : { probability: null, books: [], bookCount: 0 },
    breadth: { books: priced.length, universe: Math.max(0, Number(selectedBookCount) || 0) },
    depth: { selectionRows: priced.length, marketRows: Number(market?.rowCount) || 0 },
  }
}

function timeMinutes(value) {
  const match = String(value || '').match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i)
  if (!match) return Number.MAX_SAFE_INTEGER
  let hour = Number(match[1]) % 12
  if (match[3].toUpperCase() === 'PM') hour += 12
  return hour * 60 + Number(match[2])
}

export function sortTerminalEvents(events) {
  return [...(events || [])].sort((left, right) => {
    const date = String(left?.etDate || '').localeCompare(String(right?.etDate || ''))
    if (date) return date
    const simulation = isSimulatedLeague(left?.league) - isSimulatedLeague(right?.league)
    if (simulation) return simulation
    const time = timeMinutes(left?.etTime) - timeMinutes(right?.etTime)
    if (time) return time
    const coverage = (right?.primaryBooks?.length || right?.books?.length || 0)
      - (left?.primaryBooks?.length || left?.books?.length || 0)
    return coverage || String(left?.event || '').localeCompare(String(right?.event || ''))
  })
}

function isSimulatedLeague(value) {
  const league = String(value || '').toLowerCase()
  return Number(
    league.startsWith('ebasketball')
      || league.startsWith('esoccer')
      || league.startsWith('efootball')
      || league.includes('esports')
      || league.includes('simulat'),
  )
}
